import { useQuery } from '@tanstack/react-query'

import { diaDoPrograma, percentualPrograma, semanaDoPrograma } from './datas'
import { supabase } from './supabase'
import type { MatriculaCompleta, Perfil } from '@/tipos/tabelas'

/**
 * Contexto da sessão: quem é a participante, em que programa está e em
 * que dia. Praticamente toda tela depende disto.
 */

export const CHAVES = {
  perfil: ['perfil'] as const,
  matricula: ['matricula'] as const,
  diaPrograma: (numero: number) => ['dia-programa', numero] as const,
  praticas: (programaId: string) => ['praticas', programaId] as const,
  diario: (matriculaId: string, data: string) => ['diario', matriculaId, data] as const,
  cardapio: (programaId: string, periodo: number) => ['cardapio', programaId, periodo] as const,
  videos: (programaId: string, categoria: string) => ['videos', programaId, categoria] as const,
  lista: (matriculaId: string, semana: number) => ['lista', matriculaId, semana] as const,
  medidas: (matriculaId: string) => ['medidas', matriculaId] as const,
  diarios: (matriculaId: string) => ['diarios', matriculaId] as const,
  rodaVida: (matriculaId: string) => ['roda-vida', matriculaId] as const,
  conquistas: (matriculaId: string) => ['conquistas', matriculaId] as const,
  lembretes: (matriculaId: string) => ['lembretes', matriculaId] as const,
}

export function usePerfil() {
  return useQuery({
    queryKey: CHAVES.perfil,
    queryFn: async (): Promise<Perfil | null> => {
      const { data: sessao } = await supabase.auth.getSession()
      const userId = sessao.session?.user.id
      if (!userId) return null

      const { data, error } = await supabase
        .from('perfis')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) throw error
      return data as Perfil | null
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useMatricula() {
  return useQuery({
    queryKey: CHAVES.matricula,
    queryFn: async (): Promise<MatriculaCompleta | null> => {
      const { data: sessao } = await supabase.auth.getSession()
      const userId = sessao.session?.user.id
      if (!userId) return null

      const { data, error } = await supabase
        .from('matriculas')
        .select('*, turma:turmas(*, programa:programas(*))')
        .eq('user_id', userId)
        .eq('status', 'ativa')
        .order('data_inicio', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as MatriculaCompleta | null
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * A matrícula mais recente que NÃO está ativa — só para diferenciar,
 * na tela de "sem turma", quem nunca se matriculou de quem terminou os
 * 28 dias. `useMatricula()` continua só olhando para 'ativa': o resto
 * do app assume que uma matrícula existente está em andamento, e
 * misturar os dois conceitos ali criaria mais confusão do que resolve.
 */
export function useMatriculaEncerrada() {
  return useQuery({
    queryKey: ['matricula-encerrada'],
    queryFn: async () => {
      const { data: sessao } = await supabase.auth.getSession()
      const userId = sessao.session?.user.id
      if (!userId) return null

      const { data, error } = await supabase
        .from('matriculas')
        .select('status, turma:turmas(nome)')
        .eq('user_id', userId)
        .eq('status', 'concluida')
        .order('data_inicio', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as { status: string; turma: { nome: string } | null } | null
    },
    staleTime: 5 * 60 * 1000,
  })
}

export interface ContextoPrograma {
  matricula: MatriculaCompleta
  /** Pode ser <= 0 antes do início — ver `iniciado`. */
  dia: number
  semana: number
  percentual: number
  duracaoDias: number
  concluido: boolean
  /** false antes da data de início da turma. */
  iniciado: boolean
}

/**
 * Dia atual, semana e progresso.
 *
 * Calculado no cliente de propósito: a tela inicial precisa abrir e
 * mostrar "Dia 12 de 28" mesmo sem rede. O banco tem a mesma conta em
 * `public.dia_atual`, usada pelo agendamento de push.
 */
export function useContextoPrograma(): ContextoPrograma | null {
  const { data: matricula } = useMatricula()
  if (!matricula) return null

  const duracaoDias = matricula.turma.programa.duracao_dias
  const dia = diaDoPrograma(matricula.data_inicio, duracaoDias)

  return {
    matricula,
    dia,
    // Antes do início, "semana" não tem sentido — nunca é lida nesse
    // estado (o app mostra a tela de espera), mas evita um número
    // negativo estranho se algo ainda assim consultar o valor.
    semana: semanaDoPrograma(Math.max(1, dia)),
    percentual: percentualPrograma(dia, duracaoDias),
    duracaoDias,
    concluido: dia >= duracaoDias,
    iniciado: dia >= 1,
  }
}
